'use client';

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface FilterOption {
  id: string;
  label: string;
  count?: number;
}

interface FilterCategory {
  id: string;
  label: string;
  options: FilterOption[];
}

interface CollapsibleFilterPanelProps {
  className?: string;
}

const FILTER_CATEGORIES: FilterCategory[] = [
  {
    id: 'mode',
    label: 'Mode',
    options: [
      { id: 'question', label: 'Question', count: 18 },
      { id: 'observation', label: 'Observation', count: 24 },
      { id: 'note', label: 'Note', count: 32 },
      { id: 'article', label: 'Article', count: 15 },
      { id: 'essay', label: 'Essay', count: 8 },
    ],
  },
  {
    id: 'subject',
    label: 'Subject',
    options: [
      { id: 'technology', label: 'Technology', count: 42 },
      { id: 'design', label: 'Design', count: 35 },
      { id: 'philosophy', label: 'Philosophy', count: 28 },
      { id: 'culture', label: 'Culture', count: 31 },
      { id: 'science', label: 'Science', count: 22 },
      { id: 'art', label: 'Art', count: 19 },
    ],
  },
  {
    id: 'tone',
    label: 'Tone/Genre',
    options: [
      { id: 'satire', label: 'Satire', count: 12 },
      { id: 'humor', label: 'Humor', count: 16 },
      { id: 'fiction', label: 'Fiction', count: 8 },
      { id: 'personal', label: 'Personal', count: 28 },
      { id: 'serious', label: 'Serious', count: 34 },
      { id: 'experimental', label: 'Experimental', count: 10 },
    ],
  },
  {
    id: 'intent',
    label: 'Intent',
    options: [
      { id: 'commentary', label: 'Commentary', count: 24 },
      { id: 'criticism', label: 'Criticism', count: 18 },
      { id: 'reported', label: 'Reported', count: 14 },
      { id: 'review', label: 'Review', count: 20 },
      { id: 'tutorial', label: 'Tutorial', count: 16 },
      { id: 'interview', label: 'Interview', count: 9 },
    ],
  },
  {
    id: 'project',
    label: 'Project',
    options: [
      { id: 'digital-garden', label: 'Digital Garden', count: 15 },
      { id: 'weekly-notes', label: 'Weekly Notes', count: 28 },
      { id: 'research-log', label: 'Research Log', count: 12 },
    ],
  },
  {
    id: 'series',
    label: 'Series',
    options: [
      { id: 'thinking-aloud', label: 'Thinking Aloud', count: 22 },
      { id: 'deep-dives', label: 'Deep Dives', count: 14 },
      { id: 'quick-thoughts', label: 'Quick Thoughts', count: 31 },
    ],
  },
];

const CollapsibleFilterPanel = ({ className = '' }: CollapsibleFilterPanelProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [isHydrated, setIsHydrated] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['mode', 'subject']);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const filters: Record<string, string[]> = {};
    FILTER_CATEGORIES.forEach((category) => {
      const paramValue = searchParams.get(category.id);
      if (paramValue) {
        filters[category.id] = paramValue.split(',');
      }
    });
    setSelectedFilters(filters);
    // Auto-expand if filters are active
    if (Object.keys(filters).length > 0) {
      setIsExpanded(true);
    }
  }, [searchParams, isHydrated]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleFilterToggle = (categoryId: string, optionId: string) => {
    const currentFilters = selectedFilters[categoryId] || [];
    const newFilters = currentFilters.includes(optionId)
      ? currentFilters.filter((id) => id !== optionId)
      : [...currentFilters, optionId];

    const updatedFilters = {
      ...selectedFilters,
      [categoryId]: newFilters,
    };

    if (newFilters.length === 0) {
      delete updatedFilters[categoryId];
    }

    setSelectedFilters(updatedFilters);
    updateURL(updatedFilters);
  };

  const updateURL = (filters: Record<string, string[]>) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, values]) => {
      if (values.length > 0) {
        params.set(key, values.join(','));
      }
    });

    const queryString = params.toString();
    router.push(`${pathname}${queryString ? `?${queryString}` : ''}`, {
      scroll: false,
    });
  };

  const handleClearAll = () => {
    setSelectedFilters({});
    router.push(pathname, { scroll: false });
  };

  const getActiveFilterCount = () => {
    return Object.values(selectedFilters).reduce(
      (sum, filters) => sum + filters.length,
      0
    );
  };

  const activeFilterCount = getActiveFilterCount();

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:178:6" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="178" data-component-end-line="189" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`mb-6 bg-card rounded-lg border border-border shadow-editorial-sm ${className}`}>
        <div data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:179:8" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="179" data-component-end-line="188" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-6%20py-4%20border-b%20border-border%22%7D" className="px-6 py-4 border-b border-border">
          <div data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:180:10" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="180" data-component-end-line="187" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
            <div data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:181:12" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="181" data-component-end-line="186" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-3%22%7D" className="flex items-center space-x-3">
              <Icon data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:182:14" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="182" data-component-end-line="182" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22FunnelIcon%22%2C%22className%22%3A%22text-primary%22%7D" name="FunnelIcon" size={24} className="text-primary" />
              <h2 data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:183:14" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="183" data-component-end-line="185" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-heading%20font-semibold%20text-lg%20text-foreground%22%2C%22textContent%22%3A%22Filters%22%7D" className="font-heading font-semibold text-lg text-foreground">
                Filters
              </h2>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:194:4" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="194" data-component-end-line="306" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`mb-6 bg-card rounded-lg border border-border shadow-editorial-sm transition-smooth ${className}`}>
      {/* Header - Always Visible */}
      <div data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:196:6" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="196" data-component-end-line="220" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-6%20py-4%20border-b%20border-border%22%7D" className="px-6 py-4 border-b border-border">
        <div data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:197:8" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="197" data-component-end-line="219" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
          <button data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:198:10" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="198" data-component-end-line="218" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20space-x-3%20flex-1%20text-left%20transition-smooth%20hover%3Atext-primary%20group%22%7D"
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center space-x-3 flex-1 text-left transition-smooth hover:text-primary group"
          >
            <Icon data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:202:12" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="202" data-component-end-line="202" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22FunnelIcon%22%2C%22className%22%3A%22text-primary%22%7D" name="FunnelIcon" size={24} className="text-primary" />
            <h2 data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:203:12" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="203" data-component-end-line="205" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-heading%20font-semibold%20text-lg%20text-foreground%22%2C%22textContent%22%3A%22Filters%22%7D" className="font-heading font-semibold text-lg text-foreground">
              Filters
            </h2>
            {activeFilterCount > 0 && (
              <span data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:207:14" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="207" data-component-end-line="209" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22px-2.5%20py-0.5%20rounded-full%20bg-primary%20text-primary-foreground%20caption%20font-medium%22%7D" className="px-2.5 py-0.5 rounded-full bg-primary text-primary-foreground caption font-medium">
                {activeFilterCount}
              </span>
            )}
            <Icon data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:211:12" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="211" data-component-end-line="217" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronDownIcon%22%7D"
              name="ChevronDownIcon"
              size={20}
              className={`ml-auto transition-smooth text-muted-foreground group-hover:text-primary ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
          </button>
        </div>
      </div>

      {/* Collapsible Content */}
      {isExpanded && (
        <div data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:224:8" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="224" data-component-end-line="304" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-6%20py-6%22%7D" className="px-6 py-6">
          <div data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:225:10" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="225" data-component-end-line="291" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20xl%3Agrid-cols-6%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
            {FILTER_CATEGORIES.map((category) => {
              const isCategoryExpanded = expandedCategories.includes(category.id);
              const categoryFilters = selectedFilters[category.id] || [];

              return (
                <div data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:231:16" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="231" data-component-end-line="288" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-3%22%7D" key={category.id} className="space-y-3">
                  <button data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:232:18" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="232" data-component-end-line="246" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20flex%20items-center%20justify-between%20text-left%20transition-smooth%20hover%3Atext-primary%20group%22%7D"
                    onClick={() => toggleCategory(category.id)}
                    className="w-full flex items-center justify-between text-left transition-smooth hover:text-primary group"
                  >
                    <span data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:236:20" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="236" data-component-end-line="238" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-heading%20font-medium%20text-sm%20text-foreground%22%7D" className="font-heading font-medium text-sm text-foreground">
                      {category.label}
                    </span>
                    <Icon data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:239:20" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="239" data-component-end-line="245" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronDownIcon%22%7D"
                      name="ChevronDownIcon"
                      size={16}
                      className={`transition-smooth text-muted-foreground group-hover:text-primary ${
                        isCategoryExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {isCategoryExpanded && (
                    <div data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:249:20" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="249" data-component-end-line="286" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-2%22%7D" className="space-y-2">
                      {category.options.map((option) => {
                        const isSelected = categoryFilters.includes(option.id);

                        return (
                          <label data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:254:26" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="254" data-component-end-line="283" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20py-2%20px-3%20rounded-md%20cursor-pointer%20transition-smooth%20hover%3Abg-muted%20group%22%7D"
                            key={option.id}
                            className="flex items-center justify-between py-2 px-3 rounded-md cursor-pointer transition-smooth hover:bg-muted group"
                          >
                            <div data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:258:28" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="258" data-component-end-line="277" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20flex-1%20min-w-0%22%7D" className="flex items-center space-x-2 flex-1 min-w-0">
                              <input data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:259:30" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="259" data-component-end-line="266" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22checkbox%22%2C%22className%22%3A%22w-4%20h-4%20rounded%20border-border%20text-primary%20focus%3Aring-2%20focus%3Aring-primary%20focus%3Aring-offset-2%20transition-smooth%20cursor-pointer%20flex-shrink-0%22%7D"
                                type="checkbox"
                                checked={isSelected}
                                onChange={() =>
                                  handleFilterToggle(category.id, option.id)
                                }
                                className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-smooth cursor-pointer flex-shrink-0"
                              />
                              <span data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:267:30" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="267" data-component-end-line="276" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                                className={`caption transition-smooth truncate ${
                                  isSelected
                                    ? 'text-foreground font-medium'
                                    : 'text-muted-foreground'
                                }`}
                                title={option.label}
                              >
                                {option.label}
                              </span>
                            </div>
                            {option.count !== undefined && (
                              <span data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:279:30" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="279" data-component-end-line="281" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22caption%20text-muted-foreground%20ml-2%20flex-shrink-0%22%7D" className="caption text-muted-foreground ml-2 flex-shrink-0">
                                {option.count}
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {activeFilterCount > 0 && (
            <div data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:294:12" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="294" data-component-end-line="302" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-6%20pt-6%20border-t%20border-border%22%7D" className="mt-6 pt-6 border-t border-border">
              <button data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:295:14" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="295" data-component-end-line="301" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20flex%20items-center%20justify-center%20space-x-2%20px-4%20py-3%20rounded-md%20border%20border-border%20text-foreground%20font-medium%20transition-smooth%20hover%3Abg-muted%20hover%3A-translate-y-px%22%7D"
                onClick={handleClearAll}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-md border border-border text-foreground font-medium transition-smooth hover:bg-muted hover:-translate-y-px"
              >
                <Icon data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:299:16" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="299" data-component-end-line="299" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22XMarkIcon%22%7D" name="XMarkIcon" size={20} />
                <span data-component-id="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx:300:16" data-component-path="src/app/thoughts-feed/components/CollapsibleFilterPanel.tsx" data-component-line="300" data-component-end-line="300" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Clear%20All%20Filters%22%7D">Clear All Filters</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CollapsibleFilterPanel;









